import React from 'react';
import {
	Box,
	Grid,
	Text,
	Badge,
	Flex,
	HStack,
	VStack,
	Avatar,
	AvatarGroup,
	Button,
	Icon,
} from '@chakra-ui/react';
import { FaBullhorn, FaProjectDiagram, FaUserPlus, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { SectionCard, EmptyPanel } from './dashboardShared';
import {
	getWorkItemStatusColor,
	getWorkItemStatusLabel,
} from '../../TodoTask/WorkspaceViews/taskViewUtils';

const statusColor = (status) => {
	const normalized = String(status || '').toLowerCase();
	if (['active', 'sending', 'in progress'].includes(normalized)) return 'green';
	if (['completed', 'sent', 'won'].includes(normalized)) return 'blue';
	if (['draft', 'new', 'not started'].includes(normalized)) return 'gray';
	if (['paused', 'on hold'].includes(normalized)) return 'orange';
	return 'purple';
};

const OverviewTab = ({
	recentCampaigns = [],
	recentProjects = [],
	recentLeads = [],
	teamMembers = [],
}) => {
	const navigate = useNavigate();

	return (
		<Grid templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }} gap={6}>
			<SectionCard
				title='Recent campaigns'
				description='Latest email campaigns and engagement.'
				action={
					<Button size='sm' variant='ghost' onClick={() => navigate('/campaigns')}>
						View all
					</Button>
				}>
				{recentCampaigns.length === 0 ? (
					<EmptyPanel
						message='No campaigns yet. Create your first campaign to start reaching contacts.'
						actionLabel='Create campaign'
						onAction={() => navigate('/campaigns/create')}
					/>
				) : (
					<VStack align='stretch' spacing={3}>
						{recentCampaigns.map((campaign) => (
							<Flex
								key={campaign.id}
								justify='space-between'
								align='start'
								gap={3}
								p={3}
								borderRadius='lg'
								bg='gray.50'
								_hover={{ bg: 'teal.50', cursor: 'pointer' }}
								onClick={() => navigate(`/campaigns/${campaign.id}/analytics`)}>
								<HStack align='start' spacing={3} minW={0}>
									<Icon as={FaBullhorn} color='teal.500' mt={1} />
									<Box minW={0}>
										<Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
											{campaign.name}
										</Text>
										<Text fontSize='xs' color='gray.500'>
											Created {campaign.created} · {campaign.sent.toLocaleString()} sent
										</Text>
									</Box>
								</HStack>
								<VStack align='end' spacing={1} flexShrink={0}>
									<Badge colorScheme={statusColor(campaign.status)} borderRadius='full'>
										{campaign.status}
									</Badge>
									<Text fontSize='xs' color='gray.500'>
										{campaign.opens} opens · {campaign.clicks} clicks
									</Text>
								</VStack>
							</Flex>
						))}
					</VStack>
				)}
			</SectionCard>

			<SectionCard
				title='Project pipeline'
				description='Recently updated projects across your company.'
				action={
					<Button size='sm' variant='ghost' onClick={() => navigate('/projects')}>
						View all
					</Button>
				}>
				{recentProjects.length === 0 ? (
					<EmptyPanel
						message='No projects yet. Start tracking delivery, tasks, and team work in one place.'
						actionLabel='Create project'
						onAction={() => navigate('/projects/add')}
					/>
				) : (
					<VStack align='stretch' spacing={3}>
						{recentProjects.map((project) => (
							<Flex
								key={project.id}
								justify='space-between'
								align='start'
								gap={3}
								p={3}
								borderRadius='lg'
								bg='gray.50'
								_hover={{ bg: 'blue.50', cursor: 'pointer' }}
								onClick={() => navigate(`/projects/details/${project.id}`)}>
								<HStack align='start' spacing={3} minW={0}>
									<Icon as={FaProjectDiagram} color='blue.500' mt={1} />
									<Box minW={0}>
										<Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
											{project.title}
										</Text>
										<Text fontSize='xs' color='gray.500' noOfLines={1}>
											{project.code}
											{project.client ? ` · ${project.client}` : ''}
										</Text>
									</Box>
								</HStack>
								<Badge
									colorScheme={getWorkItemStatusColor(project.status)}
									borderRadius='full'>
									{getWorkItemStatusLabel(project.status)}
								</Badge>
							</Flex>
						))}
					</VStack>
				)}
			</SectionCard>

			<SectionCard
				title='Lead pipeline'
				description='Latest leads moving through your CRM.'
				action={
					<Button size='sm' variant='ghost' onClick={() => navigate('/leads')}>
						View all
					</Button>
				}>
				{recentLeads.length === 0 ? (
					<EmptyPanel
						message='No leads yet. Capture prospects and follow up from your CRM.'
						actionLabel='Open leads'
						onAction={() => navigate('/leads')}
					/>
				) : (
					<VStack align='stretch' spacing={3}>
						{recentLeads.map((lead) => (
							<Flex
								key={lead.id}
								justify='space-between'
								align='start'
								gap={3}
								p={3}
								borderRadius='lg'
								bg='gray.50'
								_hover={{ bg: 'orange.50', cursor: 'pointer' }}
								onClick={() => navigate(`/leads/${lead.id}`)}>
								<HStack align='start' spacing={3} minW={0}>
									<Icon as={FaUserPlus} color='orange.500' mt={1} />
									<Box minW={0}>
										<Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
											{lead.name}
										</Text>
										<Text fontSize='xs' color='gray.500' noOfLines={1}>
											{lead.company || 'No company'}
										</Text>
									</Box>
								</HStack>
								<Badge colorScheme={statusColor(lead.status)} borderRadius='full'>
									{lead.status}
								</Badge>
							</Flex>
						))}
					</VStack>
				)}
			</SectionCard>

			<SectionCard
				title='Team snapshot'
				description='People in your company workspace.'
				action={
					<Button size='sm' variant='ghost' onClick={() => navigate('/employees')}>
						View team
					</Button>
				}>
				{teamMembers.length === 0 ? (
					<EmptyPanel message='No team members found for this company.' />
				) : (
					<VStack align='stretch' spacing={4}>
						<HStack justify='space-between'>
							<Text fontSize='sm' color='gray.600'>
								{teamMembers.length} team member{teamMembers.length === 1 ? '' : 's'}
							</Text>
							<AvatarGroup size='sm' max={5}>
								{teamMembers.slice(0, 5).map((member) => (
									<Avatar key={member.id} name={member.name} src={member.avatar} />
								))}
							</AvatarGroup>
						</HStack>
						<VStack align='stretch' spacing={2}>
							{teamMembers.slice(0, 4).map((member) => (
								<Flex
									key={member.id}
									justify='space-between'
									align='center'
									p={3}
									borderRadius='lg'
									bg='gray.50'
									_hover={{ bg: 'purple.50', cursor: 'pointer' }}
									onClick={() => navigate(`/employee/view/${member.id}`)}>
									<HStack spacing={3} minW={0}>
										<Icon as={FaUsers} color='purple.500' />
										<Box minW={0}>
											<Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
												{member.name}
											</Text>
											<Text fontSize='xs' color='gray.500' noOfLines={1}>
												{member.role}
												{member.department ? ` · ${member.department}` : ''}
											</Text>
										</Box>
									</HStack>
									<Badge colorScheme='teal' borderRadius='full'>
										{member.systemRole}
									</Badge>
								</Flex>
							))}
						</VStack>
					</VStack>
				)}
			</SectionCard>
		</Grid>
	);
};

export default OverviewTab;
